import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserPlus, Bell, Clock, MessageCircle, ChevronRight, Zap, CreditCard, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { monthlyBenefits } from './constants';
import { getUsedBenefitsThisMonth, isNearExpiration } from './utils';
import { PaymentRemindersSection } from './PaymentRemindersSection';
import { AdminStatsDashboard } from './AdminStatsDashboard';
import type { MemberData } from './types';

interface ActionsPanelProps {
  members: MemberData[];
  onSelectMember: (member: MemberData) => void;
  onContactMember: (member: MemberData, message: string) => void;
  onGoToMembers: () => void;
  isAdmin?: boolean;
}

type AlertSubTab = 'activaciones' | 'beneficios' | 'pagos';

export const ActionsPanel: React.FC<ActionsPanelProps> = ({
  members,
  onSelectMember,
  onContactMember,
  onGoToMembers,
  isAdmin = false,
}) => {
  const [activeTab, setActiveTab] = useState('alertas');
  const [alertSubTab, setAlertSubTab] = useState<AlertSubTab>('activaciones');

  // Pending activations - only users who have NEVER been activated
  const pendingMembers = members.filter(
    (m) => m.membership && !m.membership.is_active && !m.membership.activated_at
  );

  // Members with unused benefits - sorted by days until membership expires (closest first)
  const membersWithUnused = members
    .filter(m => m.membership && m.membership.is_active)
    .map(member => {
      if (!member.membership) return null;
      
      const benefits = monthlyBenefits[member.membership.plan];
      const unusedBenefits = Object.entries(benefits)
        .filter(([key, benefit]) => {
          const used = getUsedBenefitsThisMonth(member.redemptions, key);
          return used < benefit.max;
        })
        .map(([key, benefit]) => {
          const used = getUsedBenefitsThisMonth(member.redemptions, key);
          return `${benefit.max - used} ${benefit.name}`;
        });
      
      if (unusedBenefits.length === 0) return null;
      
      // Calculate days until end of current month (benefit expiry)
      const now = new Date();
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const daysUntilExpiry = Math.ceil((endOfMonth.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      return { member, unusedBenefits, daysUntilExpiry };
    })
    .filter(Boolean) as { member: MemberData; unusedBenefits: string[]; daysUntilExpiry: number }[];

  // Sort by days until expiry (closest to end of month first)
  membersWithUnused.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);

  // Members with upcoming payment due (membership expiration)
  const membersWithPaymentDue = members
    .filter(m => m.membership && m.membership.is_active)
    .filter(m => {
      const status = isNearExpiration(m.membership);
      return status.isExpiring;
    });

  // Total alerts count
  const totalAlerts = pendingMembers.length + membersWithUnused.length + (isAdmin ? membersWithPaymentDue.length : 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-white/5 border border-white/10 h-12 p-1 rounded-xl">
          <TabsTrigger 
            value="alertas" 
            className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400 text-white/60 rounded-lg transition-all"
          >
            <Bell className="w-4 h-4 mr-1.5" />
            <span>Alertas</span>
            {totalAlerts > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-purple-500/30 rounded-full">
                {totalAlerts}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger 
            value="estadisticas" 
            className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 text-white/60 rounded-lg transition-all"
          >
            <BarChart3 className="w-4 h-4 mr-1.5" />
            <span>Estadísticas</span>
          </TabsTrigger>
        </TabsList>

        {/* Alertas Tab - Contains Activaciones, Beneficios, Pagos */}
        <TabsContent value="alertas" className="mt-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Sub-tabs for Alertas */}
            <div className={`grid ${isAdmin ? 'grid-cols-3' : 'grid-cols-2'} gap-2`}>
              <button
                onClick={() => setAlertSubTab('activaciones')}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                  alertSubTab === 'activaciones'
                    ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                    : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                }`}
              >
                <Zap className="w-4 h-4" />
                <span className="text-sm font-medium">Activar</span>
                {pendingMembers.length > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-blue-500/30 rounded-full">
                    {pendingMembers.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setAlertSubTab('beneficios')}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                  alertSubTab === 'beneficios'
                    ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
                    : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                }`}
              >
                <Bell className="w-4 h-4" />
                <span className="text-sm font-medium">Beneficios</span>
                {membersWithUnused.length > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-purple-500/30 rounded-full">
                    {membersWithUnused.length}
                  </span>
                )}
              </button>
              {isAdmin && (
                <button
                  onClick={() => setAlertSubTab('pagos')}
                  className={`flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                    alertSubTab === 'pagos'
                      ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                      : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  <span className="text-sm font-medium">Pagos</span>
                  {membersWithPaymentDue.length > 0 && (
                    <span className="px-1.5 py-0.5 text-xs bg-amber-500/30 rounded-full">
                      {membersWithPaymentDue.length}
                    </span>
                  )}
                </button>
              )}
            </div>

            {/* Activaciones Sub-content */}
            {alertSubTab === 'activaciones' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 backdrop-blur-xl rounded-2xl p-6 border border-blue-500/20"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <UserPlus className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">Pendientes de Activación</h3>
                    <p className="text-blue-400/80 text-sm">
                      {pendingMembers.length} cliente{pendingMembers.length !== 1 ? 's' : ''} esperando
                    </p>
                  </div>
                </div>

                {pendingMembers.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-500/10 mx-auto mb-3 flex items-center justify-center">
                      <UserPlus className="w-8 h-8 text-green-400" />
                    </div>
                    <p className="text-green-400 font-medium">¡Todos activados!</p>
                    <p className="text-white/50 text-sm">No hay clientes pendientes</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {pendingMembers.map((member) => (
                      <div
                        key={member.profile.user_id}
                        className="flex items-center justify-between bg-white/5 rounded-xl p-3 hover:bg-white/10 transition-all group"
                      >
                        <button
                          onClick={() => {
                            onSelectMember(member);
                            onGoToMembers();
                          }}
                          className="flex-1 text-left flex items-center gap-3 min-w-0"
                        >
                          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-5 h-5 text-blue-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate">
                              {member.profile.nombre}
                            </p>
                            <p className="text-white/50 text-xs">
                              Pendiente de activación
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors ml-auto flex-shrink-0" />
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const message = `¡Hola ${member.profile.nombre.split(' ')[0]}! 👋\n\n¿Cómo estás? Te escribo de VillaTech para ayudarte a activar tu membresía.\n\n¿Tenés alguna consulta o necesitás ayuda con el proceso? 🎁`;
                            onContactMember(member, message);
                          }}
                          className="ml-2 p-2.5 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors flex-shrink-0"
                          title="Contactar por WhatsApp"
                        >
                          <MessageCircle className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Beneficios Sub-content */}
            {alertSubTab === 'beneficios' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Bell className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">Recordatorios de Beneficios</h3>
                    <p className="text-purple-400/80 text-sm">
                      {membersWithUnused.length} miembro{membersWithUnused.length !== 1 ? 's' : ''} con beneficios sin usar
                    </p>
                  </div>
                </div>

                {membersWithUnused.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-500/10 mx-auto mb-3 flex items-center justify-center">
                      <Bell className="w-8 h-8 text-green-400" />
                    </div>
                    <p className="text-green-400 font-medium">¡Todos al día!</p>
                    <p className="text-white/50 text-sm">Todos los miembros han usado sus beneficios</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {membersWithUnused.map(({ member, unusedBenefits }) => (
                      <div
                        key={member.profile.user_id}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-all"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm font-medium truncate">
                            {member.profile.nombre}
                          </p>
                          <p className="text-white/50 text-xs truncate">
                            Sin usar: {unusedBenefits.slice(0, 2).join(', ')}
                            {unusedBenefits.length > 2 && ` +${unusedBenefits.length - 2}`}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const message = `¡Hola ${member.profile.nombre.split(' ')[0]}! 👋\n\nTe recordamos que tenés beneficios de tu membresía VillaTech sin usar este mes:\n\n${unusedBenefits.map(b => `• ${b}`).join('\n')}\n\n¡Visitá nuestra tienda para canjearlos antes de fin de mes! 🎁`;
                            onContactMember(member, message);
                          }}
                          className="border-green-500/30 text-green-400 hover:bg-green-500/20 shrink-0 ml-2"
                        >
                          <MessageCircle className="w-3.5 h-3.5 mr-1" />
                          WhatsApp
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Pagos Sub-content - Admin Only */}
            {isAdmin && alertSubTab === 'pagos' && (
              <PaymentRemindersSection 
                members={members}
                onContactMember={onContactMember}
              />
            )}
          </motion.div>
        </TabsContent>

        {/* Estadísticas Tab */}
        <TabsContent value="estadisticas" className="mt-6">
          <AdminStatsDashboard members={members} />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};

export default ActionsPanel;
